import {
  DomElementRendererExtension,
  type DomRenderer,
} from '@blocksuite/affine-block-surface';
import {
  ColorScheme,
  type MermaidElementModel,
} from '@blocksuite/affine-model';
import {
  MermaidRenderProvider,
  type MermaidRenderSession,
  ThemeProvider,
  translate,
} from '@blocksuite/affine-shared/services';

import {
  setMermaidInteractionSnapshot,
  setMermaidRenderResult,
} from './state.js';

const RENDERER_TAG = 'affine-edgeless-mermaid-renderer';

export class EdgelessMermaidRendererElement extends HTMLElement {
  private readonly _content: HTMLDivElement;
  private readonly _error: HTMLDivElement;
  private _request = 0;
  private _session: MermaidRenderSession | null = null;
  private _model: MermaidElementModel | null = null;
  private _renderer: DomRenderer | null = null;
  private _lastColorScheme: ColorScheme | null = null;
  private _themeDispose: (() => void) | null = null;

  constructor() {
    super();
    const root = this.attachShadow({ mode: 'open' });
    root.innerHTML = `
      <style>
        :host {
          display: block;
          width: 100%;
          height: 100%;
          box-sizing: border-box;
          overflow: hidden;
        }
        .content {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }
        .content > svg {
          width: 100%;
          height: 100%;
          display: block;
        }
        .error {
          position: absolute;
          left: 8px;
          right: 8px;
          bottom: 8px;
          padding: 4px 6px;
          border-radius: 4px;
          background: var(--affine-background-overlay-panel-color);
          color: var(--affine-text-secondary-color);
          font: 11px/16px sans-serif;
          box-shadow: var(--affine-shadow-1);
        }
        .error[hidden] {
          display: none;
        }
      </style>
      <div class="content" role="img"></div>
      <div class="error" role="status" aria-live="polite" hidden></div>
    `;
    this._content = root.querySelector('.content') as HTMLDivElement;
    this._error = root.querySelector('.error') as HTMLDivElement;
  }

  disconnectedCallback() {
    this._request++;
    this._themeDispose?.();
    this._themeDispose = null;
    this._lastColorScheme = null;
    this._model = null;
    this._renderer = null;
    this._session?.dispose();
    this._session = null;
  }

  private _bindTheme(renderer: DomRenderer) {
    if (this._renderer === renderer && this._themeDispose) return;

    this._themeDispose?.();
    this._renderer = renderer;
    this._lastColorScheme = renderer.getColorScheme();
    this._themeDispose = renderer.std
      .get(ThemeProvider)
      .edgeless$.subscribe(colorScheme => {
        if (colorScheme === this._lastColorScheme) return;
        this._lastColorScheme = colorScheme;
        const model = this._model;
        const currentRenderer = this._renderer;
        if (!model || !currentRenderer || !this.isConnected) return;
        this.update(model, currentRenderer).catch(() => undefined);
      });
  }

  private _deriveInteractionSnapshot(
    model: MermaidElementModel,
    source: string,
    renderKey: string,
    request: number
  ) {
    void import('./interaction-geometry.js')
      .then(({ buildMermaidInteractionSnapshotFromSvgElement }) => {
        if (request !== this._request || !this.isConnected) return;
        const svg = this._content.querySelector('svg');
        const snapshot =
          svg instanceof SVGSVGElement
            ? buildMermaidInteractionSnapshotFromSvgElement(
                svg,
                source,
                renderKey
              )
            : null;
        if (request !== this._request || !this.isConnected) return;
        setMermaidInteractionSnapshot(model, snapshot);
      })
      .catch(() => {
        if (request === this._request) {
          setMermaidInteractionSnapshot(model, null);
        }
      });
  }

  async update(model: MermaidElementModel, renderer: DomRenderer) {
    this._model = model;
    this._bindTheme(renderer);

    const diagramLabel =
      translate(renderer.std, 'Mermaid diagram') ?? 'Mermaid diagram';
    this.setAttribute('role', 'img');
    this.setAttribute('aria-label', diagramLabel);
    this._content.setAttribute('aria-label', diagramLabel);

    const provider = renderer.std.getOptional(MermaidRenderProvider);
    if (!provider) {
      const unavailable =
        translate(renderer.std, 'Mermaid renderer unavailable') ??
        'Mermaid renderer unavailable';
      this._content.textContent = unavailable;
      this.setAttribute('aria-label', diagramLabel + ': ' + unavailable);
      return;
    }

    this._session ??= provider.createSession();
    const request = ++this._request;
    const source = model.source.toString();
    const result = await this._session.render({
      source,
      themeMode: model.themeMode,
      prefersDark: renderer.getColorScheme() === ColorScheme.Dark,
      visible: this.isConnected,
      zoom: renderer.viewport.zoom,
    });

    if (request !== this._request || !this.isConnected) return;

    setMermaidRenderResult(model, source, result);

    switch (result.status) {
      case 'ready':
      case 'degraded':
        this._content.innerHTML = result.svg;
        this._deriveInteractionSnapshot(
          model,
          source,
          JSON.stringify({
            source,
            themeMode: model.themeMode,
            prefersDark: renderer.getColorScheme() === ColorScheme.Dark,
          }),
          request
        );
        this._error.hidden = true;
        this._error.textContent = '';
        this.setAttribute('aria-label', diagramLabel);
        break;
      case 'invalid':
      case 'error':
        setMermaidInteractionSnapshot(model, null);
        if (result.svg) {
          this._content.innerHTML = result.svg;
        }
        this._error.textContent = result.message;
        this._error.hidden = false;
        this.setAttribute(
          'aria-label',
          diagramLabel + ': ' + (result.message || 'render error')
        );
        break;
      case 'empty': {
        setMermaidInteractionSnapshot(model, null);
        const emptyLabel =
          translate(renderer.std, 'Empty Mermaid diagram') ??
          'Empty Mermaid diagram';
        this._content.textContent = emptyLabel;
        this._error.hidden = true;
        this.setAttribute('aria-label', diagramLabel + ': ' + emptyLabel);
        break;
      }
      case 'deferred':
      case 'superseded':
        break;
    }
  }
}

export function mermaidDomRenderer(
  model: MermaidElementModel,
  domElement: HTMLElement,
  renderer: DomRenderer
) {
  let element =
    domElement.firstElementChild as EdgelessMermaidRendererElement | null;

  if (!(element instanceof EdgelessMermaidRendererElement)) {
    element = document.createElement(
      RENDERER_TAG
    ) as EdgelessMermaidRendererElement;
    domElement.replaceChildren(element);
  }

  element.update(model, renderer).catch(error => {
    console.error('Failed to render Mermaid element', error);
  });
}

export const MermaidDomRendererExtension = DomElementRendererExtension(
  'mermaid',
  mermaidDomRenderer
);

export const MermaidRendererTag = RENDERER_TAG;
